import { stopwatch } from '@abyss/web/tools/stopwatch';
import { Button } from '@abyss/web/ui/Button';
import { Layout } from '@abyss/web/ui/Layout';
import { Modal } from '@abyss/web/ui/Modal';
import { storage } from '@abyss/web/tools/storage';
import React, { useEffect, useRef, useState } from 'react';

import { heartbeat } from 'api/src/services/heartbeat';
import { dropSsoSession } from 'api/src/services/dropSsoSession';
import { FINDCARE_AND_COST_SSO_SESSION_COOKIE } from './utils';
import { config } from '@abyss/web/tools/config';
import { useLagoon } from '../../hooks/useLagoon';
import { find } from 'lodash';
import {
  ConstantsLagoon,
  SessionInitStatus,
  StoreKeys,
  TIMEOUT_MODAL_DEFAULTS,
} from '../../common/constants';
import { parseSessionCookie } from '../../../../commonUtils/src/sessionManager';
import { setLogout } from '@/hooks/setLogout';
import { useSessionStorage } from 'usehooks-ts';
import {
  EmptySSOPortalEligibilityData,
  SSOPortalEligibilityData,
} from 'api/src/dataModels/SSOPortalEligibilityData';
import { INTERNAL_ERROR_PATH } from '../../appConstants';

interface AppRouter {
  navigate: (path: string) => void;
}

interface Props {
  router: AppRouter | undefined;
  initStatus: keyof typeof SessionInitStatus;
  showSignedOutModal: boolean;
  setShowSignedOutModal: (val: boolean) => void;
}

export const TimeoutModal = ({
  router,
  initStatus,
  showSignedOutModal,
  setShowSignedOutModal,
}: Props) => {
  const containerConfigData = useLagoon('container-config')();
  const sessionTimeoutModalIdleOffsetSecs =
    parseInt(
      find(containerConfigData, {
        key: ConstantsLagoon.CONTAINER_CONFIG
          .SESSION_TIMEOUT_MODAL_IDLE_OFFSET_SECS,
      })?.value
    ) || TIMEOUT_MODAL_DEFAULTS.IDLE_OFFSET_SECS;

  const sessionTimeoutModalLogoutCountdownSecs =
    parseInt(
      find(containerConfigData, {
        key: ConstantsLagoon.CONTAINER_CONFIG
          .SESSION_TIMEOUT_MODAL_LOGOUT_COUNTDOWN_SECS,
      })?.value
    ) || TIMEOUT_MODAL_DEFAULTS.LOGOUT_COUNTDOWN_SECS;

  const sessionTimeoutModalRefreshDelaySecs =
    parseInt(
      find(containerConfigData, {
        key: ConstantsLagoon.CONTAINER_CONFIG
          .SESSION_TIMEOUT_MODAL_REFRESH_DELAY_SECS,
      })?.value
    ) || TIMEOUT_MODAL_DEFAULTS.REFRESH_DELAY_SECS;

  const ssoSessionCookie = storage.cookie.get(
    FINDCARE_AND_COST_SSO_SESSION_COOKIE
  );
  const cookie = parseSessionCookie(ssoSessionCookie);
  const { durationSecs } = cookie;

  const IDLE_MILLIS = (durationSecs - sessionTimeoutModalIdleOffsetSecs) * 1000;
  const LOGOUT_MILLIS = sessionTimeoutModalLogoutCountdownSecs * 1000;
  const REFRESH_DELAY_SECONDS = sessionTimeoutModalRefreshDelaySecs;

  const containerUiMessagingData = useLagoon('container-ui-messaging')();
  const inactivityModalTitle = find(containerUiMessagingData, {
    key: ConstantsLagoon.CONTAINER_UI_MESSAGING.INACTIVITY_MODAL_TITLE,
  })?.message;
  const inactivityModalRenewSession = find(containerUiMessagingData, {
    key: ConstantsLagoon.CONTAINER_UI_MESSAGING.INACTIVITY_MODAL_RENEW_SESSION,
  })?.message;
  const inactivityModalSignOut = find(containerUiMessagingData, {
    key: ConstantsLagoon.CONTAINER_UI_MESSAGING.INACTIVITY_MODAL_SIGN_OUT,
  })?.message;
  const inactivityModalBodyLine1 = find(containerUiMessagingData, {
    key: ConstantsLagoon.CONTAINER_UI_MESSAGING.INACTIVITY_MODAL_BODY_LINE_1,
  })?.message?.replace('{idleMinutes}', (IDLE_MILLIS / 60000).toString());
  const inactivityModalBodyLine2 = find(containerUiMessagingData, {
    key: ConstantsLagoon.CONTAINER_UI_MESSAGING.INACTIVITY_MODAL_BODY_LINE_2,
  })?.message?.replace('{signoutMinutes}', (LOGOUT_MILLIS / 60000).toString());

  const ACTIONS = ['mousedown', 'focus', 'keydown'];

  const [isInactive, setIsInactive] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(true);
  let timer;

  const addEvents = (listener) => {
    ACTIONS.forEach((eventName) => {
      window.addEventListener(eventName, listener);
    });
  };

  const removeEvents = (listener) => {
    ACTIONS.forEach((eventName) => {
      window.removeEventListener(eventName, listener);
    });
  };

  const heartbeatCheck = (config) => {
    heartbeat(config).catch((error) => {
      if (error?.status >= 400 && error?.status < 600) {
        router?.navigate(INTERNAL_ERROR_PATH);
      }
    });
  };

  // Don't attempt to refresh session if last attempt was less than REFRESH_DELAY_SECONDS seconds ago
  const shouldDoHeartbeat = () => {
    if (timer?.stop() > REFRESH_DELAY_SECONDS) {
      heartbeatCheck(config);
      timer = stopwatch.start();
    }
  };

  const handleRenewSession = () => {
    heartbeatCheck(config);
    setIsInactive(false);
  };

  const handleLogout = () => {
    setOpenModal(false);
    setIsInactive(false);
    const ssoPortalEligibilityDataNotEmpty =
      ssoPortalEligibilityData !== EmptySSOPortalEligibilityData;
    if (ssoPortalEligibilityDataNotEmpty) {
      dropSsoSession(config).catch((error) => {
        if (error?.status >= 400 && error?.status < 600) {
          router?.navigate(INTERNAL_ERROR_PATH);
        }
      });
    }
    setShowSignedOutModal(true);
  };

  useEffect(() => {
    heartbeatCheck(config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    timer = stopwatch.start();
  }, []);

  const [shouldLogout, setShouldLogout] = useState(false);
  const [shouldRefreshTimer, setShouldRefreshTimer] = useState(false);
  const ssoCookieRef = useRef<string>(
    sessionStorage.getItem(FINDCARE_AND_COST_SSO_SESSION_COOKIE) || ''
  );
  const [ssoPortalEligibilityData] =
    useSessionStorage<SSOPortalEligibilityData>(
      StoreKeys.SSO_PORTAL_ELIGIBILITY_DATA,
      EmptySSOPortalEligibilityData
    );

  useEffect(() => {
    let interval;
    if (!showSignedOutModal) {
      interval = setInterval(() => {
        const { shouldLogout, shouldResetTimer, latestCookie } = setLogout(
          initStatus,
          ssoPortalEligibilityData?.uniqueMemberId,
          ssoCookieRef.current
        );
        if (shouldLogout) {
          sessionStorage.clear();
          setShowSignedOutModal(true);
        }
        ssoCookieRef.current = latestCookie;
        setShouldLogout(shouldLogout);
        setShouldRefreshTimer(shouldResetTimer);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [
    ssoPortalEligibilityData?.uniqueMemberId,
    initStatus,
    router,
    showSignedOutModal,
    setShowSignedOutModal,
    ssoPortalEligibilityData,
  ]);

  useEffect(() => {
    if (showSignedOutModal) {
      return () => {};
    }
    const idleTimeout = () =>
      setTimeout(() => {
        setIsInactive(true);
      }, IDLE_MILLIS);

    const logoutTimeout = () =>
      setTimeout(() => {
        handleLogout();
      }, LOGOUT_MILLIS);

    const refreshTimer = () => {
      shouldDoHeartbeat();
      clearTimeout(timeout);
      timeout = idleTimeout();
    };

    let timeout = isInactive ? logoutTimeout() : idleTimeout();

    const listener = () => {
      if (!isInactive) {
        refreshTimer();
      }
    };

    addEvents(listener);

    if (shouldRefreshTimer) {
      refreshTimer();
      setIsInactive(false);
    }

    if (shouldLogout) {
      setOpenModal(false);
    }

    return () => {
      removeEvents(listener);
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInactive, shouldRefreshTimer, shouldLogout]);

  if (!isInactive || showSignedOutModal) {
    return null;
  }

  return (
    <Modal
      closeOnClickOutside={false}
      closeOnEscPress={false}
      isOpen={openModal}
      onClose={handleRenewSession}
      size="md"
      title={inactivityModalTitle}
    >
      <Modal.Section>{inactivityModalBodyLine1}</Modal.Section>
      <Modal.Section
        // @ts-expect-error used to display markdown content correctly from lagoon
        dangerouslySetInnerHTML={{ __html: inactivityModalBodyLine2 }}
      />
      <Modal.Section>
        <Layout.Group>
          <Button
            data-auto-testid="timeout-modal-renew"
            data-testid="timeout-modal-renew"
            onClick={handleRenewSession}
          >
            {inactivityModalRenewSession}
          </Button>
          <Button
            data-auto-testid="timeout-modal-logout"
            data-testid="timeout-modal-logout"
            onClick={handleLogout}
            variant="outline"
          >
            {inactivityModalSignOut}
          </Button>
        </Layout.Group>
      </Modal.Section>
    </Modal>
  );
};
